import { useState, useEffect, useRef, useCallback } from 'react';
import { productCategories } from '../data/siteData';
import './Products.css';

const SLIDE_INTERVAL = 3000;
const isImage = (val) => val && /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(val);

const BADGE_COLORS = {
  'Best Seller': { bg: '#FF6B2C', text: '#fff' },
  'New':         { bg: '#2E7D32', text: '#fff' },
  'Organic':     { bg: '#00695C', text: '#fff' },
};

/* ─── Product Card ─────────────────────────────── */
function ProductCard({ p, catColor, isActive }) {
  const [flipped, setFlipped] = useState(false);
  const badge = p.badge ? BADGE_COLORS[p.badge] : null;
  useEffect(() => { if (!isActive) setFlipped(false); }, [isActive]);

  return (
    <div
      className={`pcard${flipped ? ' pcard--flipped' : ''}`}
      style={{ '--accent': p.color || '#E8F5E9', '--cat': catColor }}
    >
      {/* FRONT */}
      <div className="pcard__front">

        {/* TOP HALF — full bleed image zone */}
        <div className="pcard__hero">
          <div className="pcard__hero-bg" />

          <div className="pcard__hero-tags">
            <span className="pcard__type-tag">{p.type}</span>
            {badge && (
              <span className="pcard__badge" style={{ background: badge.bg, color: badge.text }}>
                {p.badge}
              </span>
            )}
          </div>

          {isImage(p.image) ? (
            <>
              <img src={p.image} alt={p.name} className="pcard__img" />
              <div className="pcard__img-shadow" />
            </>
          ) : (
            <span className="pcard__emoji">{p.emoji || '🌿'}</span>
          )}
        </div>

        {/* BOTTOM HALF — info */}
        <div className="pcard__body">
          <h4 className="pcard__name">{p.name}</h4>
          <div className="pcard__rows">
            {[
              { label: 'Active', val: p.active },
              { label: 'Crops',  val: p.crops  },
              { label: 'Pack',   val: p.pack   },
            ].map(r => (
              <div className="pcard__row" key={r.label}>
                <span className="pcard__lbl">{r.label}</span>
                <span className="pcard__val">{r.val}</span>
              </div>
            ))}
          </div>
          <button className="pcard__btn" onClick={() => setFlipped(true)}>
            View Details →
          </button>
        </div>
      </div>

      {/* BACK */}
      <div className="pcard__back">
        <div className="pcard__back-header" style={{ background: p.color || '#E8F5E9' }}>
          {isImage(p.image)
            ? <img src={p.image} alt={p.name} className="pcard__back-img" />
            : <span style={{ fontSize: '2.2rem' }}>{p.emoji || '🌿'}</span>
          }
          <div>
            <div className="pcard__back-name">{p.name}</div>
            <div className="pcard__back-type">{p.type}</div>
          </div>
        </div>
        <div className="pcard__back-body">
          <p className="pcard__feat-label">Key Features</p>
          <ul className="pcard__feats">
            {(p.details || []).map(d => (
              <li key={d}><span className="pcard__dot" />{d}</li>
            ))}
          </ul>
          <div className="pcard__meta">
            <div className="pcard__meta-row">
              <span>Dosage</span><span>{p.dosage || 'See label'}</span>
            </div>
            <div className="pcard__meta-row">
              <span>Pre-Harvest</span><span>{p.waitDays || 'See label'}</span>
            </div>
          </div>
          <div className="pcard__back-btns">
            <a href="#contact" className="pcard__quote">Get Quote</a>
            <button className="pcard__back-close" onClick={() => setFlipped(false)}>← Back</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Carousel ─────────────────────────────────── */
function CategoryCarousel({ cat }) {
  const [cur, setCur]       = useState(0);
  const [paused, setPaused] = useState(false);
  const timer               = useRef(null);
  const total               = cat.products.length;

  const next = useCallback(() => setCur(c => (c + 1) % total), [total]);
  const prev = useCallback(() => setCur(c => (c - 1 + total) % total), [total]);

  useEffect(() => {
    clearInterval(timer.current);
    if (!paused) timer.current = setInterval(next, SLIDE_INTERVAL);
    return () => clearInterval(timer.current);
  }, [paused, next, cur]);

  const offset = (i) => {
    let d = i - cur;
    if (d >  total / 2) d -= total;
    if (d < -total / 2) d += total;
    return d;
  };

  return (
    <div
      className="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="carousel__header">
        <div className="carousel__icon" style={{ background: cat.colorLight, color: cat.color }}>
          {cat.icon}
        </div>
        <div className="carousel__info">
          <h3 className="carousel__title">{cat.heading}</h3>
          <p className="carousel__desc">{cat.desc}</p>
        </div>
        <div className="carousel__count" style={{ color: cat.color }}>
          {String(cur + 1).padStart(2, '0')}
          <span className="carousel__count-of"> / {String(total).padStart(2, '0')}</span>
        </div>
      </div>

      <div className="carousel__stage">
        <button className="carousel__arrow" onClick={prev} style={{ '--c': cat.color }}>‹</button>
        <div className="carousel__track">
          {cat.products.map((p, i) => {
            const off    = offset(i);
            const active = off === 0;
            return (
              <div key={p.name} className="carousel__slide" style={{
                zIndex:        active ? 10 : Math.abs(off) === 1 ? 5 : 1,
                opacity:       Math.abs(off) > 2 ? 0 : active ? 1 : 0.32,
                transform:     `translateX(calc(${off} * 106%)) scale(${active ? 1 : 0.85})`,
                pointerEvents: active ? 'auto' : 'none',
                transition:    'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease',
              }}>
                <ProductCard p={p} catColor={cat.color} isActive={active} />
              </div>
            );
          })}
        </div>
        <button className="carousel__arrow" onClick={next} style={{ '--c': cat.color }}>›</button>
      </div>

      <div className="carousel__dots">
        {cat.products.map((_, i) => (
          <button key={i} onClick={() => setCur(i)}
            className={`carousel__dot${i === cur ? ' active' : ''}`}
            style={{ '--c': cat.color }} />
        ))}
      </div>

      <div className="carousel__progress">
        <div
          key={`${cat.id}-${cur}-${paused}`}
          className={`carousel__bar${paused ? ' paused' : ''}`}
          style={{ '--c': cat.color, '--dur': `${SLIDE_INTERVAL}ms` }}
        />
      </div>
    </div>
  );
}

/* ─── Section ──────────────────────────────────── */
export default function Products() {
  const [active, setActive] = useState(0);
  const cat = productCategories[active];

  /* ── Activate a tab by category id ── */
  const activateById = useCallback((id) => {
    const idx = productCategories.findIndex(c => c.id === id);
    if (idx !== -1) setActive(idx);
  }, []);

  /* ── Listen for footer CustomEvent ── */
  useEffect(() => {
    const handler = (e) => activateById(e.detail);
    window.addEventListener('selectProductCategory', handler);
    return () => window.removeEventListener('selectProductCategory', handler);
  }, [activateById]);

  /* ── Deep-link support: ?category=insecticide or #products-insecticide ── */
  useEffect(() => {
    const readFromUrl = () => {
      const search = new URLSearchParams(window.location.search).get('category');
      const hash   = window.location.hash.replace('#products-', '');
      activateById(search || hash);
    };
    readFromUrl();
    window.addEventListener('hashchange', readFromUrl);
    return () => window.removeEventListener('hashchange', readFromUrl);
  }, [activateById]);

  return (
    <section id="products" className="section products">
      <span className="section-eyebrow">Product Range</span>
      <h2 className="section-title">Four Pillars of<br /><em>Crop Protection</em></h2>
      <p className="products__intro">
        Explore our complete portfolio — hover any slide to pause, flip cards for full technical details.
      </p>

      {/* Tabs */}
      <div className="products__tabs">
        {productCategories.map((c, i) => (
          <button
            key={c.id}
            className={`products__tab${active === i ? ' active' : ''}`}
            style={{ '--c': c.color }}
            onClick={() => setActive(i)}
          >
            <span className="products__tab-icon">{c.icon}</span>
            <span className="products__tab-label">{c.label}</span>
            <span className="products__tab-pill">{c.products.length}</span>
          </button>
        ))}
      </div>

      {/* Carousel panel — key resets slide to 0 on tab change */}
      <div className="products__panel" key={cat.id} style={{ '--c': cat.color, '--cl': cat.colorLight }}>
        <CategoryCarousel cat={cat} />
      </div>

      {/* Active category products tile strip */}
      <div className="products__tiles-wrap">
        <div className="products__tiles-label">All Products in {cat.label}</div>
        <div className="products__tiles">
          {cat.products.map(p => (
            <div key={p.name} className="products__tile" style={{ '--c': cat.color }}>
              <span className="products__tile-icon">
                {isImage(p.image) ? <img src={p.image} alt={p.name} /> : p.emoji || '🌿'}
              </span>
              <div>
                <div className="products__tile-name">{p.name}</div>
                <div className="products__tile-type">{p.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}